import { Injectable } from '@angular/core';
import { Observable, of, Subject, Subscription, timer } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SendGridMessagesPage } from '@interfaces/dws/statistic-campaign';
import { DownloadType } from '@interfaces/dws/statistic-campaign';

/** TTL after a successful SendGrid messages response; then cache is cleared and {@link silentRefreshRequested$} emits. */
const SENDGRID_MESSAGES_TTL_MS = 3 * 60 * 1000;

/**
 * Frontend cache + single timer for CRM email/download SendGrid endpoints.
 * - Same request params within TTL: returns cached rows (no HTTP).
 * - Any new request (different key): clears cache, cancels timer, fetches; on success restarts 3m timer.
 * - When TTL elapses: clears cache, emits {@link silentRefreshRequested$} so UI can refetch (e.g. campaign contacts drawer).
 * - Full page reload: new service instance → timer and cache reset.
 */
@Injectable({ providedIn: 'root' })
export class SendGridMessagesCacheService {
  private readonly cache = new Map<string, { data: SendGridMessagesPage; storedAt: number }>();
  private ttlSubscription: Subscription | null = null;

  /** Subscribe in campaign SendGrid UI; on TTL expiry refetch primary list (e.g. first page of contacts). */
  readonly silentRefreshRequested$ = new Subject<void>();

  /**
   * Returns cached copy if key matches and age &lt; TTL; otherwise clears all cache + timer, then runs {@code network$}.
   * Timer is (re)started only after a successful {@code network$} emission.
   *
   * @param skipCacheRead when true (TTL silent refresh): always call {@code network$}, cancel timer only, do not clear
   * cache until success — lets UI keep showing previous rows until the new response arrives.
   */
  runWithCache(
    cacheKey: string,
    network$: Observable<SendGridMessagesPage>,
    options?: { skipCacheRead?: boolean }
  ): Observable<SendGridMessagesPage> {
    if (!options?.skipCacheRead) {
      const entry = this.cache.get(cacheKey);
      const now = Date.now();
      if (entry && now - entry.storedAt < SENDGRID_MESSAGES_TTL_MS) {
        return of(this.clonePage(entry.data));
      }
      this.invalidateAllAndCancelTimer();
    } else {
      this.cancelTtlTimer();
    }

    return network$.pipe(
      tap({
        next: (data) => {
          if (options?.skipCacheRead) {
            this.cache.clear();
          }
          this.cache.set(cacheKey, { data: this.clonePage(data), storedAt: Date.now() });
          this.startTtlTimer();
        },
      })
    );
  }

  /** Clears cache and cancels TTL timer (e.g. on destroy of SendGrid-heavy views — optional). */
  invalidateAllAndCancelTimer(): void {
    this.cache.clear();
    this.cancelTtlTimer();
  }

  static buildKey(
    wineryId: string,
    categoryId: string,
    type: DownloadType,
    toEmail?: string,
    limit?: number,
    lastEventTimeBefore?: string,
    searchText?: string
  ): string {
    return [
      wineryId,
      categoryId,
      String(type),
      toEmail ?? '',
      limit ?? '',
      lastEventTimeBefore ?? '',
      searchText ?? '',
    ].join('\u001e');
  }

  private startTtlTimer(): void {
    this.cancelTtlTimer();
    this.ttlSubscription = timer(SENDGRID_MESSAGES_TTL_MS).subscribe(() => {
      this.ttlSubscription = null;
      this.silentRefreshRequested$.next();
    });
  }

  private cancelTtlTimer(): void {
    this.ttlSubscription?.unsubscribe();
    this.ttlSubscription = null;
  }

  private clonePage(page: SendGridMessagesPage): SendGridMessagesPage {
    return {
      messages: (page?.messages || []).map((r) => ({ ...r })),
      nextCursor: page?.nextCursor ?? null,
      reason: page?.reason ?? null,
    };
  }
}
